<template>
    <div id="wp-contact-list-page">
        <h3 class="title" v-if="showTitle">{{ $t("wp.contactList") }}</h3>
        <div v-if="checkboxOption.selectedRowKeys.length" class="selection-bar">
            <section class="uncheck" @click="unselectAll">
                <span class="material-icons"> arrow_back </span>
                {{ $t("chatbotv2.uncheckAll") }}
            </section>
            <section class="select" @click="selectAll">{{ $t("chatbotv2.selectAll") }}</section>
            <section class="select" @click="deleteAll">{{ $t("integration.deleteAll") }}</section>
            <section class="queries">
                +{{ `${checkboxOption.selectedRowKeys.length} ${$t("wp.contactSelected")}` }}
            </section>
            <section class="actions">
                <a-button class="d-none" ghost disabled>{{ $t("wp.sendBroadcastMessage") }}</a-button>
            </section>
        </div>
        <div class="toolbox">
            <div>
                <slot name="left-actions"></slot>
            </div>
            <div>
                <slot name="middle-actions"></slot>
            </div>
            <div class="buttons">
                <SupsisButton
                    v-if="allowImport"
                    @click="() => (importMode = true)"
                    primary
                    :disabled="!channel || forbidden"
                >
                    {{ $t("wp.importContactsFromFile") }}
                </SupsisButton>
                <SupsisButton @click="showWhatsappChatStarterModal = true" ghost>
                    {{ $t("wp.startChatWithAnyNo") }}
                </SupsisButton>
                <a-dropdown v-if="channel && channel.isDeleted">
                    <SupsisButton xxsmall icon>
                        <i class="material-icons text-warning">more_horiz</i>
                    </SupsisButton>
                    <a-menu slot="overlay">
                        <a-menu-item key="transfer" @click="showTransferModal()">
                            <span>Kişileri Başka Kanala Transfer Et</span>
                        </a-menu-item>
                    </a-menu>
                </a-dropdown>
            </div>
        </div>
        <div class="table-wrapper">
            <div class="table-pagination">
                <a-input-search
                    style="width: 300px"
                    v-model="searchText"
                    :placeholder="$t('message.search')"
                    class="row search-input"
                    @input="onSearch($event.target.value)"
                />
                <ve-pagination
                    :total="pagination.total"
                    :page-index="pagination.current"
                    :page-size="pagination.defaultPageSize"
                    :page-size-option="pagination.pageSizeOptions"
                    @on-page-number-change="pageNumberChange"
                    @on-page-size-change="pageSizeChange"
                />
            </div>
            <ve-table
                :columns="cols"
                :table-data="allContacts"
                :row-style-option="rowStyleOption"
                :border-x="true"
                :border-y="true"
                row-key-field-name="_id"
                id="visitor-table"
                :fixed-header="true"
                :checkbox-option="checkboxOption"
                :clipboard-option="clipboardOption"
            />
            <NoMessage title="Forbidden Channel" desc="You are not allowed to see this channel" v-if="forbidden" />
        </div>
        <Modal2
            v-if="showWhatsappChatStarterModal"
            @cancel="showWhatsappChatStarterModal = false"
            :title="$t('message.whatsappChatStart')"
            :showFooter="false"
        >
            <WhatsappChatStarter
                channel-override
                @close="closeModal"
                :default-channel-id="channelId === 'all' ? undefined : channelId"
            />
        </Modal2>
        <Modal2
            title="Import Contacts"
            :show-footer="false"
            v-if="importMode"
            @cancel="importMode = false"
            class-name="import-contact-modal"
        >
            <ImportWhatsappContactsFromFile :on-cancelled="() => (importMode = false)" :on-completed="importContacts" />
        </Modal2>
        <Modal2
            title="Transfer Contacts"
            :show-footer="false"
            v-if="transferModalVisible"
            @cancel="transferModalVisible = false"
            class-name="transfer-contact-modal"
        >
            <TransferContacts
                :channel-id="channelId"
                :target-channel-id.sync="targetChannelId"
                @transfer="handleTransfer"
                @cancel="transferModalVisible = false"
            />
        </Modal2>
    </div>
</template>
<script>
import _ from "lodash";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import Vue from "vue";
import VueTheMask from "vue-the-mask";
import log2 from "../../../utils/log2";
import Modal2 from "../../../components/common/Modal2.vue";
import ImportWhatsappContactsFromFile from ".././WhatsappBusinessGupshup/import/ImportWhatsappContactsFromFile.vue";
import { socketRequest } from "../../../api/hostFetcher";
import { VeLocale, VePagination } from "vue-easytable";
import enUS from "vue-easytable/libs/locale/lang/en-US.js";
import moment from "moment";
import WhatsappIcon from "../../../components/svg-new-ui/WhatsappIcon.vue";
import SupsisButton from "../Settings/components/SupsisButton.vue";
import NoMessage from "../TeamChat/NoMessage.vue";
import ChatWatcher from "../../../ChatWatcherV2";
import { isMaskContactInfo, maskContactInfo } from "../../../utils/utils";
import { getWhatsappContactList } from "../../../api/chat";
import WhatsappChatStarter from "../Integrations/meta-leads/WhatsappChatStarter.vue";
import AiVoiceAgentCallStarter from "../../../components/AiVoiceAgentCallStarter.vue";
import TransferContacts from "./TransferContacts.vue";
import SYSTEM_PUBSUB from "../../../utils/systemPubSub";

Vue.use(VePagination);
VeLocale.use(enUS);
Vue.use(VueTheMask);
const log = log2("WhatsappContactList");
export default {
    name: "WhatsappContactList",
    components: {
        NoMessage,
        ImportWhatsappContactsFromFile,
        Modal2,
        SupsisButton,
        WhatsappIcon,
        WhatsappChatStarter,
        AiVoiceAgentCallStarter,
        TransferContacts,
    },

    props: {
        allowImport: {
            type: Boolean,
            default: true,
        },
        channel: {
            type: Object,
        },
        channelId: {
            type: String,
            default: null,
        },
        showTitle: {
            type: Boolean,
            default: true,
        },
        forbidden: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            importMode: false,
            transferModalVisible: false,
            targetChannelId: null,
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            showWhatsappChatStarterModal: false,
            showAlertMessage: false,
            searchText: "",
            pagination: {
                total: 0,
                current: 1,
                defaultPageSize: 50,
                hideOnSinglePage: true,
                pageSizeOptions: [10, 15, 20, 50, 100],
                showQuickJumper: true,
                showSizeChanger: true,
            },
            cols: [
                {
                    field: "",
                    key: "a",
                    type: "checkbox",
                    title: "",
                    width: 50,
                    align: "center",
                },
                {
                    field: "address",
                    key: "profilePicThumbObj",
                    title: this.$t("message.profile"),
                    width: 50,
                    align: "center",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "a-avatar",
                            {
                                props: {
                                    size: 30,
                                    src: row.profilePicThumbObj?.eurl,
                                    username: row.name,
                                    shape: "square",
                                },
                                style: "background-color: #3899ff; border-radius: 7px !important",
                            },
                            row.name.substr(0, 1).toLocaleUpperCase()
                        );
                    },
                },
                {
                    key: "name",
                    field: "name",
                    title: this.$t("message.name"),
                    width: "",
                    align: "left",
                },
                {
                    key: "phoneNumber",
                    field: `phoneNumber`,
                    title: this.$t("message.number"),
                },
                {
                    key: "email",
                    field: `email`,
                    title: this.$t("message.email"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{row.email || "-"}</span>;
                    },
                },
                {
                    key: "channelName",
                    field: `channelName`,
                    title: this.$t("message.channel"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{row.channel?.name || "-"}</span>;
                    },
                },
                {
                    key: "createdAt",
                    field: `createdAt`,
                    title: this.$t("message.createdAt"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{moment(row.createdAt).format("DD.MM.YYYY HH:mm")}</span>;
                    },
                },
                {
                    title: this.$t("message.action"),
                    key: "operation",
                    width: "140px",
                    align: "center",
                    operationColumn: true,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return (
                            <div class="actions">
                                <SupsisButton
                                    onClick={() => this.startChat(row.whatsappNumber || row.phone, row.channel, row.whatsappId)}
                                    small
                                >
                                    <WhatsappIcon />
                                </SupsisButton>
                                <AiVoiceAgentCallStarter
                                    contact={row}
                                    defaultToNumber={row.whatsappNumber || row.phone}
                                    hideButton={false}
                                />
                                <SupsisButton 
                                    onClick={() => this.startWebphoneCall(row)} 
                                    small
                                    class="webphone-call-btn"
                                    v-show={row.phone || row.whatsappNumber}
                                    content="Webphone ile Ara"
                                    v-tippy={{ followCursor: true, placement: "bottom-end" }}
                                >
                                    <i class="material-icons">phone</i>
                                </SupsisButton>
                                <SupsisButton onClick={() => this.deleteVisitorConfirm(row)} small>
                                    <i class="material-icons text-danger">delete</i>
                                </SupsisButton>
                                <SupsisButton onClick={() => this.viewContactProfile(row)} small>
                                    <i class="material-icons text-primary">contact_page</i>
                                </SupsisButton>
                            </div>
                        );
                    },
                },
            ],
            loading: true,
            loadingInstance: null,
            allContacts: [],
            checkboxOption: {
                selectedRowKeys: [],
                // row select change event
                selectedRowChange: ({ row, isSelected, selectedRowKeys }) => {
                    console.log("selectedRowChange", { row, isSelected, selectedRowKeys });
                    this.checkboxOption.selectedRowKeys = selectedRowKeys;
                },
                // selected all change event
                selectedAllChange: ({ isSelected, selectedRowKeys }) => {
                    console.log("selectedAllChange", { isSelected, selectedRowKeys });
                    this.checkboxOption.selectedRowKeys = selectedRowKeys;
                },
            },
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            siteId: (state) => state.site.site._id,
            user: (state) => state.auth.user,
            userLastCountry: (state) => _.get(state.auth.user, "lastGeoLocation.country"),
            channelInfo: (state) => state.whatsapp.channelInfo,
            whatsappChannels: (state) =>
                state.channel.channels.filter((x) =>
                    ["whatsapp-qr", "whatsapp-v2", "whatsapp-unipile", "whatsapp-business", "api"].includes(x.type)
                ),
        }),

        ...mapGetters({
            isDarkMode: "user/isDarkMode",
            myActiveConversations: "chat/getMyActiveConversations",
        }),
    },
    methods: {
        ...mapActions({
            getSettings: "setting/getSettings",
            deleteVisitor: "visitor/deleteVisitor",
            deleteMultiVisitor: "visitor/deleteMultiVisitor",
            addNewWindow: "app/addNewWindow",
            moveContactsToOtherChannel: "visitor/moveContactsToOtherChannel",
        }),
        ...mapMutations({
            changePurchaseStatus: "whatsapp/changePurchaseStatus",
            setSearchingResultWPContact: "whatsapp/setSearchingResultWPContact",
        }),
        isMaskContactInfo,
        maskContactInfo(visitor, key, length = 4) {
            return this.isMaskContactInfo(key) ? maskContactInfo(visitor[key], length) : visitor[key];
        },
        closeModal() {
            this.showWhatsappChatStarterModal = false;
            this.$emit("close");
        },
        pageNumberChange(pageIndex) {
            this.pagination.current = pageIndex;
            this.fetchContacts();
        },
        pageSizeChange(pageSize) {
            this.pagination.pageSize = pageSize;
            this.fetchContacts();
        },

        importContacts(contacts) {
            log("importContacts", contacts);
            this.importMode = false;
            socketRequest
                .post(`/${this.siteId}/${this.user?._id}/${this.channelId}/import-whatsapp-contacts`, contacts)
                .then((res) => {
                    this.$message.success("Import started successfully");
                })
                .catch((err) => {
                    this.$message.error("Import not started");
                });
        },
        selectAll() {
            this.checkboxOption.selectedRowKeys = this.allContacts.map((x) => x._id);
        },
        deleteAll() {
            this.$confirm({
                title: "Seçili Kişileri sil",
                content: "Seçili Kişileri silmek istediğinizden emin misiniz?",
                okText: "Sil",
                okType: "danger",
                cancelText: "Kapat",
                onOk: () => {
                    this.deleteMultiVisitor(this.checkboxOption.selectedRowKeys).then((isSuccess) => {
                        if (isSuccess) {
                            this.$message.success(`${this.checkboxOption.selectedRowKeys.length} kişi silindi`);
                            this.allContacts = this.allContacts.filter(
                                (x) => !this.checkboxOption.selectedRowKeys.includes(x._id)
                            );
                            this.checkboxOption.selectedRowKeys = [];
                            this.refresh();
                        }
                    });
                },
            });
        },
        unselectAll() {
            this.checkboxOption.selectedRowKeys = [];
        },
        deleteVisitorConfirm(item) {
            this.$confirm({
                title: "Kişiyi sil",
                content: "Kişiyi slimek istediğinizden emin misiniz?",
                okText: "Sil",
                okType: "danger",
                cancelText: "Kapat",
                onOk: () => {
                    this.deleteVisitor(item._id).then((res) => {
                        this.$message.success("Kişi silindi");
                        this.allContacts = this.allContacts.filter((x) => x._id !== item._id);
                    });
                },
            });
        },
        viewContactProfile(item) {
            this.addNewWindow({
                type: "show-visitor",
                // title: `${item.name} / ${item.phoneNumber} Profile`,
                title: this.$t("task.whatsappContactProfileTitle", { name: item.name, number: item.phoneNumber }),
                payload: {
                    visitorId: item._id,
                },
                footer: false,
                sliderOptions: {
                    nextAvailable: !!this.allContacts[this.allContacts.indexOf(item) + 1],
                    prevAvailable: !!this.allContacts[this.allContacts.indexOf(item) - 1],
                    next: () => {
                        log("next called");
                        const index = this.allContacts.findIndex((x) => x._id === item._id);
                        this.viewContactProfile(this.allContacts[index + 1]);
                    },
                    prev: () => {
                        log("prev called");
                        const index = this.allContacts.findIndex((x) => x._id === item._id);
                        this.viewContactProfile(this.allContacts[index - 1]);
                    },
                },
            });
        },
        startChat(whatsappNumber, channel, whatsappId) {
            log("startChat", { whatsappNumber, channel });
            const channelId = channel?._id;
            const isBusiness = channel?.type === "whatsapp-business-api" || channel?.type === "whatsapp-business-api-supsis";
            const isWhatsappV2 = channel?.type === "whatsapp-v2";
            const isWhatsappV1 = channel?.type === "whatsapp-v1";
            if (isWhatsappV1 && this.channelInfo[channelId]?.status?.status?.STATE !== "CONNECTED") {
                this.$message.error("Whatsapp bağlantısı yok"); // todo translate
                return;
            }
            if (isWhatsappV2 && this.channelInfo[channelId]?.status?.state !== "READY") {
                this.$message.error("Whatsapp bağlantısı yok"); // todo translate
                return;
            }
            if (isWhatsappV1 || isWhatsappV2) {
                whatsappNumber = whatsappNumber || "";
                whatsappNumber = whatsappNumber.includes("@c.us") ? whatsappNumber : whatsappNumber + "@c.us";
            }

            ChatWatcher.startChatImmediately({
                whatsappNumber: whatsappNumber,
                whatsappId: whatsappId,
                channelId: channel?._id,
                channelType: channel?.type,
            });
            this.$emit("close");
        },
        refresh: _.debounce(function () {
            this.loading = true;
            setTimeout(() => {
                this.loading = false;
            }, 500);
            this.pagination.current = 1;
            this.fetchContacts();
        }, 400),

        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
            this.refresh();
        },
        showModal() {
            this.showWhatsappChatStarter = true;
        },
        async fetchContacts() {
            if (this.forbidden) {
                this.loading = false;
                this.allContacts = [];
                this.pagination.total = 0;
                this.searchText = "";
                return;
            }
            this.loading = true;
            const params = {
                page: this.pagination.current,
                limit: this.pagination.pageSize || this.pagination.defaultPageSize || 10,
                search: this.searchText,
            };
            if (this.channelId && this.channelId !== "all") {
                params.channelId = this.channelId;
            }
            const allContacts = await getWhatsappContactList(
                params.channelId,
                params.page,
                params.limit,
                params.search
            );

            console.log("allContacts request:", allContacts);

            this.allContacts = allContacts.result.map((x) => {
                x.channelName = !!x.channel ? x.channel.name : "-";
                x.name = !!x.fullname ? x.fullname : "-";
                x.phoneNumber = !!x.whatsappNumber ? x.whatsappNumber : "-";
                return x;
            });
            this.allContacts = this.allContacts.map((x) => {
                x.phoneNumber = this.maskContactInfo(x, "phone", 5);
                x.email = this.maskContactInfo(x, "email", 5);
                return x;
            });
            this.pagination.total = allContacts.totalResult;
            this.loading = false;
        },
        showTransferModal() {
            this.transferModalVisible = true;
        },
        handleTransfer() {
            if (!this.targetChannelId) {
                this.$message.error("Lütfen bir hedef kanal seçin");
                return;
            }

            this.$confirm({
                title: "Kişileri Transfer Et",
                content: "Tüm kişileri seçilen kanala taşımak istediğinizden emin misiniz?",
                okText: "Transfer Et",
                okType: "primary",
                cancelText: "İptal",
                onOk: () => {
                    this.transferContacts();
                },
            });
        },
        async transferContacts() {
            try {
                const response = await this.moveContactsToOtherChannel({
                    sourceChannelId: this.channelId,
                    targetChannelId: this.targetChannelId,
                });
                console.log("transferContacts => response", response);

                if (response.success) {
                    this.$message.success("Kişiler başarıyla transfer edildi");
                    this.transferModalVisible = false;
                    this.targetChannelId = null;
                    this.fetchContacts();
                } else {
                    this.$message.error("Transfer işlemi başarısız oldu");
                }
            } catch (error) {
                this.$message.error(
                    "Transfer işlemi sırasında bir hata oluştu: " + (error.message || "Bilinmeyen hata")
                );
            }
        },
        startWebphoneCall(contact) {
            const phoneNumber = contact.phone || contact.whatsappNumber;
            
            if (!phoneNumber) {
                this.$message.warning("Aranacak numara bulunamadı");
                return;
            }

            // Webphone'a arama başlatma event'i gönder
            SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.WEBPHONE_CALL_START, {
                phoneNumber: phoneNumber,
                contactName: contact.fullname || contact.name || "Bilinmeyen",
                contactId: contact._id
            });

            this.$message.success(`Webphone ile ${phoneNumber} aranıyor...`);
        },
    },
    async mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".table-wrapper"),
            name: "wave",
        });
        this.loading = !!this.channel && !this.forbidden;
        if (this.loading) this.loadingInstance.show();
        await this.fetchContacts();
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
        channelId(val) {
            this.searchText = "";
            this.pagination.current = 1;
            this.fetchContacts();
        },
    },
};
</script>

<style lang="scss">
#visitor-table {
    .ve-table-body-td {
        padding: 0 2px !important;
    }

    .actions {
        display: flex;
        gap: 5px;

        .supsis-button {
            background: none;
            border: none;

            svg {
                width: 20px;
                height: 20px;
            }
        }

        /* AI Voice Agent Button Styling */
        .ai-voice-agent-call-starter {
            display: inline-flex;

            .conversation-actions-button {
                padding: 4px;
                background: none;
                border: none;
                cursor: pointer;

                .material-icons {
                    font-size: 20px;
                }
            }
        }

        /* Webphone Call Button Styling */
        .webphone-call-btn {
            background-color: #34c759 !important;
            color: white !important;
            border: none !important;
            
            &:hover {
                background-color: #2fb653 !important;
                color: white !important;
                transform: scale(1.05);
                box-shadow: 0 2px 6px rgba(52, 199, 89, 0.3);
            }
            
            .material-icons {
                color: white !important;
                font-size: 18px;
            }
        }
    }
}
</style>
<style scoped lang="scss">
#wp-contact-list-page {
    margin: 30px;

    .table-pagination {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 0;
    }

    .toolbox {
        display: flex;
        justify-content: space-between;

        .buttons {
            display: flex;
            gap: 10px;
        }
    }

    .delete-chat {
        background: none;
        border: none;
        margin-right: 5px;

        &:hover {
            background-color: #c0bfbf;
            border-radius: 10px;
        }
    }

    .ant-modal-body {
        padding: 24px;
        font-size: 14px;
        line-height: 1.5;
        word-wrap: break-word;
        display: flex !important;
        flex-direction: row !important;
        height: 80px !important;
    }

    .ant-table-row {
        border-bottom: 1px solid lightgrey;
    }

    .ant-table table {
        border-collapse: collapse !important;
    }

    .ant-table-small {
        border: none !important;
    }

    .ant-table-body > table > .ant-table-tbody > tr > td {
        border: none !important;
    }

    .ant-table-body > table {
        border-right: none !important;
        border-left: none !important;
        line-height: 10px !important;
    }

    .ant-table-body > table > .ant-table-thead > tr > th {
        border: none !important;
    }

    .ant-table-small {
        .ant-table-tbody {
            td {
                padding: 2px 2px !important;
                height: 10px !important;
            }

            .ant-table-row {
                height: 10px !important;
            }
        }
    }

    .selection-bar {
        align-items: center;
        background: #006cff;
        color: #fff;
        display: flex;
        flex-wrap: wrap;
        height: 50px;
        left: 0;
        padding: 10px 30px;
        position: static;
        right: 0;

        section.uncheck {
            align-items: center;
            cursor: pointer;
            display: flex;
            grid-area: uncheck;
            transition: all 0.3s;

            &:hover {
                opacity: 0.8 !important;
            }

            .material-icons {
                font-size: 15px;
                margin-right: 10px;
                margin-top: 2px;
            }
        }

        section.select {
            cursor: pointer;
            grid-area: select;
            margin-left: 20px;
            transition: all 0.3s;

            &:hover {
                opacity: 0.8 !important;
            }
        }

        section.queries {
            grid-area: queries;
            margin-left: auto;
        }

        section.actions {
            align-items: center;
            display: flex;
            grid-area: actions;
            justify-content: center;
            margin-left: 20px;
            gap: 5px;

            div {
                align-items: center;
                border: 1px solid #fff;
                border-radius: 100%;
                cursor: pointer;
                display: flex;
                height: 26px;
                justify-content: center;
                margin: 0 5px;
                transition: opacity 0.3s;
                width: 26px;

                .material-icons {
                    color: #fff;
                    font-size: 15px;
                }
            }
        }
    }

    .wp-list-searching-input {
        margin: 5px 5px !important;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        text-align: center;
    }

    .inputs {
        border: 1px solid #e5e5e5;
        position: relative;
        border-radius: 6px;
        -webkit-box-shadow: 0 0 1px #00000080;
        box-shadow: 0 0 1px #00000080;
    }
}
</style>
<style lang="scss">
.import-contact-modal {
    .ant-modal {
        width: 91vw !important;
        top: 30px !important;
        left: 30px !important;
    }

    .ant-modal-content {
        height: 95vh !important;
    }

    .ant-modal-body,
    .custom-modal-content {
        height: 100%;
    }
}
</style>
